// Subtype editor-state types. A subtype is composed in the editor from a `SubtypeDefinition`
// (static: how the subtype looks and which properties it exposes) plus per-instance
// `properties`. The generated `Subtype` enum is the discriminator.
import type { ReactElement, ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { Subtype } from '@/api/model'

export { Subtype }

// Const object (not a TS `enum`) because the build runs with `erasableSyntaxOnly`.
export const PropertyTypes = {
  text: 0,
  boolean: 1,
  integer: 2,
  complex: 3,
} as const
export type PropertyTypes = (typeof PropertyTypes)[keyof typeof PropertyTypes]

export type PropertyType = string | boolean | number | object | [] | null

// The persisted subtype minus the fields the editor manages itself; extra keys are the
// definition-specific properties, accessed dynamically during load/save.
export interface EditorModuleSubtype {
  id: number | null
  [key: string]: unknown
}

export interface SubtypeProperty {
  /** Key used in request/response and editor-state models. */
  name: string
  /** i18n key (namespace `action-modules-wizard`) for the label shown to the user. */
  displayNameKey: string
  /**
   * Namespace-qualified help key (see `helpKey()`) for the info popover next to the label.
   * Optional because `hidden` properties render no row at all — everything the user can see
   * carries one.
   */
  helpKey?: string
  /** Which built-in editor to use, or `complex` for a custom one. */
  propertyType: PropertyTypes
  /** Value used when a fresh subtype is created. */
  defaultValue?: PropertyType
  /**
   * Persist-only property: seeded, hydrated on load and sent on save, but not rendered as a
   * form row. Used for backend-`required` fields the editor doesn't expose (e.g. a link's
   * `serviceModuleType`) — omitting them would trip the backend's "missing required property".
   */
  hidden?: boolean
  /**
   * Render a TranslateButton next to this property's editor. The backend stores the content
   * translation under `subtype<subtypeId>.<name>`, so this only works for properties the
   * backend actually translates (see `ActionModuleEntityToResponseLogic`).
   */
  translatable?: boolean
  /** Custom editor; if absent a built-in is chosen from `propertyType`. */
  editor?: (props: PropertyEditorProps) => ReactElement
  /** Optional editor-state → API mapping (for `complex` properties). */
  complexEditorStateToModel?: (value: PropertyType) => unknown
  /** Optional API → editor-state mapping (for `complex` properties). */
  complexModelToEditorState?: (value: unknown) => PropertyType
}

export interface PropertyEditorProps {
  property: SubtypeProperty
  subtype: SubtypeState
  /** All subtypes in the module (including `subtype` itself) — used by the condition editors. */
  allSubtypes: SubtypeState[]
  /** Buffered content translations of the module's subtypes — used by editors of translatable rows. */
  translations: SubtypeTranslations
  onChanged: (newState: PropertyType | undefined) => void
  value: PropertyType
}

/**
 * Access to the editor-wide translation buffer, handed down to the subtype editors. Keys are
 * built by `subtypeTranslationKey` (id-free — nested ids may only exist after the module save).
 */
export interface SubtypeTranslations {
  /** Action-module id as string — undefined until the module was created. */
  entityId?: string
  get: (key: string) => Record<string, string> | undefined
  set: (key: string, translations: Record<string, string>) => void
}

export interface SubtypeState {
  definition: SubtypeDefinition
  subtype: EditorModuleSubtype
  properties: Record<string, PropertyType>
  index: number
  editorId: number
}

/**
 * Defines how a specific subtype is implemented: its name, icon, the configurable properties
 * (the editor builds the settings form from these) and the preview/toolbox components.
 * The incentivation-page detail/summary views from the old portal are intentionally omitted —
 * they belong to the not-yet-migrated incentivation feature.
 */
export interface SubtypeDefinition {
  /** i18n key (namespace `action-modules-wizard`) for the subtype's display name. */
  nameKey: string
  /**
   * Namespace-qualified help key (see `helpKey()`) explaining what this building block does.
   * Shown twice: as a popover on the toolbox chip (so the choice is informed before the drag)
   * and as visible text above the settings form once the subtype is selected.
   */
  helpKey?: string
  type: Subtype
  icon: ReactNode
  editableProperties: SubtypeProperty[]
  // `t` is threaded in (not read via a hook) because these render as plain function calls
  // inside loops — same convention as the incentivation subtype renderers.
  toolboxPlaceholder: (t: TFunction) => ReactNode
  editorPreview: (state: SubtypeState, t: TFunction) => ReactElement
}
